import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Bot, RotateCcw, SendHorizontal, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { mensagemDeErro, useEnviarMensagem } from "./api";
import { Markdown } from "./Markdown";

interface Mensagem {
  role: "user" | "assistant";
  content: string;
  erro?: boolean;
}

function Avatar({ role }: { role: Mensagem["role"] }) {
  const Icone = role === "assistant" ? Bot : User;
  return (
    <div
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
        role === "assistant" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
      )}
      aria-hidden
    >
      <Icone className="h-4 w-4" />
    </div>
  );
}

/** Porte de chat.html / AgenteChatCtrl. Rota sem item de menu (acesso só por URL, como no legado). */
export default function AgenteChat() {
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [texto, setTexto] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const enviar = useEnviarMensagem();
  const fimRef = useRef<HTMLDivElement>(null);
  const carregando = enviar.isPending;

  useEffect(() => {
    fimRef.current?.scrollIntoView?.({ block: "end" });
  }, [mensagens, carregando]);

  function enviarMensagem() {
    const message = texto.trim();
    if (!message || carregando) return;
    setMensagens((m) => [...m, { role: "user", content: message }]);
    setTexto("");
    enviar.mutate(
      { message, conversationId },
      {
        onSuccess: (resp) => {
          setConversationId(resp.conversationId);
          setMensagens((m) => [...m, { role: "assistant", content: resp.message }]);
        },
        onError: (error) => setMensagens((m) => [...m, { role: "assistant", content: mensagemDeErro(error), erro: true }]),
      },
    );
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  }

  function novaConversa() {
    setMensagens([]);
    setConversationId(null);
    setTexto("");
  }

  return (
    <section className="flex h-[calc(100vh-8rem)] min-h-[28rem] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <header className="flex items-center justify-between gap-4 border-b border-border px-5 py-3">
        <div className="flex items-center gap-3">
          <Avatar role="assistant" />
          <div>
            <h2 className="text-sm font-semibold text-foreground">Agente SILPI</h2>
            <p className="text-xs text-muted-foreground">Análise Financeira — Integração Conta Azul</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={novaConversa}>
          <RotateCcw className="mr-1.5 h-4 w-4" />
          Nova conversa
        </Button>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4" role="log" aria-live="polite">
        {mensagens.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Bot className="h-7 w-7" aria-hidden />
            </div>
            <h3 className="text-base font-semibold text-foreground">Como posso ajudar?</h3>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              Descreva o problema de cobrança que precisa analisar. Sempre informe o <strong>código ou CNPJ</strong> da
              instituição.
            </p>
          </div>
        )}

        {mensagens.map((msg, i) => (
          <div key={i} className={cn("flex items-start gap-2.5", msg.role === "user" && "flex-row-reverse")}>
            <Avatar role={msg.role} />
            <div
              className={cn(
                "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm",
                msg.role === "user" && "whitespace-pre-wrap bg-primary text-primary-foreground",
                msg.role === "assistant" && !msg.erro && "bg-muted text-foreground",
                msg.erro && "border border-destructive/30 bg-destructive/10 text-destructive",
              )}
            >
              {msg.role === "assistant" && !msg.erro ? <Markdown>{msg.content}</Markdown> : msg.content}
            </div>
          </div>
        ))}

        {carregando && (
          <div className="flex items-start gap-2.5">
            <Avatar role="assistant" />
            <div className="flex gap-1 rounded-2xl bg-muted px-4 py-3.5" aria-label="O agente está respondendo">
              {[0, 150, 300].map((delay) => (
                <span key={delay} className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: `${delay}ms` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={fimRef} />
      </div>

      <footer className="border-t border-border px-5 py-3">
        <div className="flex items-end gap-2">
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={carregando}
            rows={1}
            aria-label="Mensagem para o agente"
            placeholder="Descreva o problema (informe o código ou CNPJ da instituição)..."
            className="[field-sizing:content] max-h-40 min-h-10 flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          />
          <Button size="icon" onClick={enviarMensagem} disabled={!texto.trim() || carregando} aria-label="Enviar" title="Enviar (Enter)">
            <SendHorizontal className="h-4 w-4" />
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Enter para enviar • Shift+Enter para nova linha • Sempre informe o código ou CNPJ da instituição
        </p>
      </footer>
    </section>
  );
}
